/* =============================================================
   ServiceDetailPage — /visa/:country/:visaType/:serviceId
   Individual page for each visa service option
   Shows full content + Захиалах button
   ============================================================= */
import { useParams, useLocation } from "wouter";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowLeft, CheckCircle2, Clock, Users, Star,
  BookOpen, FileText, Mic, RefreshCw, Briefcase,
  GraduationCap, ShoppingCart, Phone,
} from "lucide-react";
import Header from "@/components/Header";
import { useConsultation } from "@/contexts/ConsultationModalContext";
import OrderModal from "@/components/OrderModal";

/* ── Service content database ─────────────────────────────────── */
interface Step { title: string; desc: string }
interface ServiceContent {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  duration: string;
  audience: string;
  description: string;
  steps: Step[];
  includes: string[];
  price?: string;
  qpayLink?: string;
  isPrimary?: boolean;
}

const SERVICE_DB: Record<string, Record<string, Record<string, ServiceContent>>> = {
  travel: {
    "self-guide": {
      usa: {
        icon: BookOpen,
        title: "АНУ аяллын виз өөрөө мэдүүлэх",
        subtitle: "DS-160, ярилцлага, статус, эрсдэл, бонд — бүрэн заавар",
        duration: "Нэн даруй нэвтрэх",
        audience: "Өөрөө мэдүүлэхийг хүсэгчид",
        description:
          "АНУ аяллын визийг өөрөө амжилттай мэдүүлэхэд шаардлагатай бүх мэдлэг, заавар, загвар баримт бичгийг агуулсан иж бүрэн заавар. Мэргэжлийн зөвлөхүүдийн туршлагад суурилсан.",
        steps: [
          { title: "DS-160 бөглөх зааварчилгаа", desc: "DS-160 маягтыг буруу бөглөх нь шууд татгалзалд хүргэдэг. Зорилгоо зөв тодорхойлох, логик зөрчилгүй, алдаагүй бөглөх бүрэн заавар." },
          { title: "Ярилцлагын бэлтгэл", desc: "Элчин сайдын яаманд ярилцлага өгөхөд бэлтгэх — нийтлэг асуулт, зөв хариулт, биеэ авч явах зөвлөмж." },
          { title: "Статус солих", desc: "Аяллын визтэй байхдаа өөрт тохиромжтой статус руу хуулийн дагуу шилжих боломж, нөхцөл, дэс дараалал." },
          { title: "Эрсдлээ бууруулах", desc: "Татгалзалд хүргэдэг нийтлэг шалтгаанууд, тэдгээрээс зайлсхийх арга, магадлалаа нэмэгдүүлэх зөвлөмж." },
          { title: "БОНД-оо найдвартай буцааж авах", desc: "Визийн бондоо хэрхэн найдвартай, хурдан буцааж авах — алхам алхмаар заавар, нийтлэг асуудлуудын шийдэл." },
        ],
        includes: [
          "DS-160 бүрэн бөглөх заавар",
          "Ярилцлагын бэлтгэлийн заавар",
          "Статус солих чиглүүлэг",
          "Эрсдэл бууруулах зөвлөмж",
          "Бонд буцааж авах заавар",
          "Нэг удаагийн асуулт хариулт",
        ],
        price: "₮59,000",
        qpayLink: "https://paylink.mn/api/s/inv_ms0umsxje35cbb",
      },
      default: {
        icon: BookOpen,
        title: "Аяллын виз өөрөө мэдүүлэх гарын авлага",
        subtitle: "Алхам алхмаар дэс дараалалтай бүрэн заавар",
        duration: "Нэн даруй нэвтрэх",
        audience: "Өөрөө мэдүүлэхийг хүсэгчид",
        description:
          "Аяллын визийг өөрөө амжилттай мэдүүлэхэд шаардлагатай бүх мэдлэг, заавар, загвар баримт бичгийг агуулсан иж бүрэн гарын авлага. Мэргэжлийн зөвлөхүүдийн туршлагад суурилсан.",
        steps: [
          { title: "Дэс дараалал", desc: "Визийн мэдүүлгийн бүрэн алхам алхмаар заавар — эхнээс эцэс хүртэл." },
          { title: "Цаг авах", desc: "Элчин сайдын яаманд цаг захиалах систем, зөвлөмж, хамгийн богино хугацаанд цаг авах арга." },
          { title: "Аяллын төлөвлөгөө", desc: "Визийн шаардлага хангасан аяллын төлөвлөгөө хэрхэн боловсруулах — загвар, жишээ." },
          { title: "Буудал захиалга", desc: "Элчин сайдын яаманд хүлээн зөвшөөрөгдөх буудлын захиалгын баримт бэлдэх." },
          { title: "Бүрдүүлэх материал", desc: "Улс бүрт тохирсон бүрэн баримт бичгийн жагсаалт, шаардлага, форматын заавар." },
        ],
        includes: [
          "Бүрэн алхам алхмаар заавар (PDF + онлайн)",
          "Аяллын төлөвлөгөөний загвар",
          "Буудлын захиалгын загвар",
          "Баримт бичгийн жагсаалт",
          "Нийтлэг алдааны жагсаалт",
          "Нэг удаагийн асуулт хариулт",
        ],
        price: "₮49,000",
      },
    },
    ds160: {
      default: {
        icon: FileText,
        title: "DS-160 бөглөх заавар",
        subtitle: "Алдаагүй, зөв бөглөх бүрэн гарын авлага",
        duration: "Нэн даруй нэвтрэх",
        audience: "DS-160 маягт бөглөхийг хүсэгчид",
        description:
          "DS-160 маягтыг буруу бөглөх нь шууд татгалзалд хүргэдэг. Зорилгоо буруу тодорхойлох, логик зөрчил, алдаатай мэдээлэл — эдгээр нийтлэг алдаанаас зайлсхийж, магадлалаа нэмэгдүүлэх бүрэн заавар.",
        steps: [
          { title: "Алдаагүй бөглөх", desc: "Нийтлэг алдаанууд, тэдгээрээс зайлсхийх арга, зөв форматын заавар." },
          { title: "Зорилгоо зөв тодорхойлох", desc: "Аяллын зорилгоо зөв, итгэлтэй тодорхойлох — татгалзалд хүргэдэг хариултуудаас зайлсхийх." },
          { title: "Магадлалаа нэмэх", desc: "Хариултуудаараа визийн магадлалаа хэрхэн нэмэгдүүлэх — мэргэжлийн зөвлөмж." },
        ],
        includes: [
          "DS-160 бүрэн бөглөх заавар",
          "Нийтлэг алдааны жагсаалт",
          "Зорилго тодорхойлох загвар",
          "Магадлал нэмэгдүүлэх зөвлөмж",
          "Нэг удаагийн асуулт хариулт",
        ],
        price: "₮89,000",
        qpayLink: "https://paylink.mn/api/s/inv_ms0uo5ng6e319a",
      },
    },
    "mock-interview": {
      default: {
        icon: Mic,
        title: "Ярилцлаганд бэлтгэх — Mock Interview",
        subtitle: "Манай зөвлөхтэй 45 минут бэлдэх",
        duration: "45 минут (онлайн)",
        audience: "Ярилцлагад орохоор бэлдэж буй хүмүүс",
        description:
          "Визийн ярилцлага нь олон хүнд стресстэй байдаг. Манай мэргэжлийн зөвлөхтэй 45 минутын mock interview хийж, бодит ярилцлагад бэлэн болно. Англи болон Монгол хэлний аль алинаар бэлдэх боломжтой.",
        steps: [
          { title: "Цаг захиалах", desc: "Танд тохиромжтой цагт онлайн уулзалт захиална." },
          { title: "Бэлтгэл хийх", desc: "Зөвлөх таны нөхцөл байдалд тулгуурлан бэлтгэл хийнэ." },
          { title: "Mock Interview", desc: "45 минутын бодит ярилцлагын дасгал — зөвлөх нь элчин сайдын ажилтны үүрэг гүйцэтгэнэ." },
          { title: "Санал хүсэлт", desc: "Ярилцлагын дараа дэлгэрэнгүй санал хүсэлт, сайжруулах зөвлөмж." },
        ],
        includes: [
          "45 минутын онлайн mock interview",
          "Англи эсвэл Монгол хэлний сонголт",
          "Нийтлэг асуулт, зөв хариулт",
          "Биеэ авч явах зөвлөмж",
          "Ярилцлагын дараах санал хүсэлт",
          "Нэмэлт асуулт хариулт (7 хоног)",
        ],
        price: "₮99,000",
        qpayLink: "https://paylink.mn/api/s/inv_ms0uqhps055031",
        isPrimary: false,
      },
    },
    "status-change": {
      default: {
        icon: RefreshCw,
        title: "Статус солих — Чиглүүлэг",
        subtitle: "Хуулийн дагуу статус шилжүүлэх иж бүрэн заавар",
        duration: "Нэн даруй нэвтрэх",
        audience: "Статус солихыг хүсэгчид",
        description:
          "Аяллын визтэй байхдаа өөрт тохиромжтой статус руу хуулийн дагуу шилжих боломж байдаг. Боломжит статусуудын тайлбар, шаардлага, дэс дараалалыг агуулсан иж бүрэн чиглүүлэг.",
        steps: [
          { title: "Боломжит статусууд", desc: "Аяллын визнээс шилжих боломжтой статусуудын тайлбар, шаардлага." },
          { title: "Шилжилтийн нөхцөл", desc: "Хэзээ, хэрхэн статус солих боломжтой — хуулийн шаардлага." },
          { title: "Дэс дараалал", desc: "Статус солих өргөдлийн алхам алхмаар заавар." },
          { title: "Хуулийн зөвлөх", desc: "Шаардлагатай тохиолдолд хуулийн зөвлөхтэй холбох." },
        ],
        includes: [
          "Боломжит статусуудын иж бүрэн тайлбар",
          "Шилжилтийн нөхцөл, шаардлага",
          "Алхам алхмаар заавар",
          "Баримт бичгийн жагсаалт",
          "Нэг удаагийн зөвлөгөө",
        ],
        price: "₮119,000",
        qpayLink: "https://paylink.mn/api/s/inv_ms0us4do864510",
      },
    },
    "full-service": {
      default: {
        icon: Star,
        title: "Биднээс үйлчилгээ авах — Бүрэн дэмжлэг",
        subtitle: "Бүх үйлчилгээг манай агент таны өмнөөс хийж өгнө",
        duration: "Захиалгаас эхлэн",
        audience: "Бүрэн дэмжлэг хүсэгчид",
        description:
          "Дээрх бүх үйлчилгээнүүдийг манай мэргэжлийн агент таньтай хамтран хийж өгнө. Та зөвхөн баримт бичгээ бэлдэж өгнө — бусдыг бид хариуцна. Мөн ажлын виз, ногоон карт авах боломжуудыг хамтран судалж болно.",
        steps: [
          { title: "Анхны зөвлөгөө", desc: "Таны нөхцөл байдлыг үнэлж, хамгийн тохиромжтой стратеги боловсруулна." },
          { title: "DS-160 бөглөх", desc: "Манай агент таны мэдээлэлд үндэслэн DS-160 маягтыг алдаагүй бөглөнө." },
          { title: "Ярилцлагад бэлдэх", desc: "Mock interview болон бэлтгэл хийж, ярилцлагад итгэлтэй орно." },
          { title: "Бүрэн дагалдах", desc: "Мэдүүлгийн явцад бүх асуулт, бэрхшээлийг шийдвэрлэнэ." },
          { title: "Нэмэлт боломжууд", desc: "Ажлын виз, ногоон карт авах боломжуудыг хамтран судална." },
        ],
        includes: [
          "Аяллын виз өөрөө мэдүүлэх гарын авлага",
          "DS-160 бөглөх бүрэн дэмжлэг",
          "Mock Interview (45 мин)",
          "Статус солих зөвлөгөө",
          "Ажлын виз, ногоон карт боломжийн судалгаа",
          "Хязгааргүй асуулт хариулт (3 сар)",
          "Татгалзсан тохиолдолд дахин мэдүүлэх дэмжлэг",
        ],
        price: "₮850,000",
        isPrimary: true,
      },
    },
  },
  study: {
    "self-guide": {
      default: {
        icon: BookOpen,
        title: "Сургалтын виз өөрөө мэдүүлэх зааварчилгаа",
        subtitle: "Сургуулиас эхлэн визийн мэдүүлэг хүртэл бүрэн заавар",
        duration: "Нэн даруй нэвтрэх",
        audience: "Гадаадад суралцахыг хүсэгчид",
        description:
          "Гадаадад суралцах мөрөөдлөө биелүүлэхэд шаардлагатай бүх мэдлэг, заавар, тооцоолол. Сургуулиа зөв сонгохоос эхлэн визийн мэдүүлэг хүртэл иж бүрэн гарын авлага.",
        steps: [
          { title: "Сургуулиа зөв сонгох", desc: "Байршил, зардал, чанар, элсэлтийн шаардлагад үндэслэн сургуулиа сонгох арга." },
          { title: "Хэлний курсээр виз авах", desc: "Хэлний курсийн визээр гадаадад суралцах боломж, арга зам." },
          { title: "Дэс дараалал", desc: "Сургалтын визийн мэдүүлгийн бүрэн алхам алхмаар заавар." },
          { title: "Бүрдүүлэх материал", desc: "Улс, сургуулийн төрлөөр ялгаатай баримт бичгийн бүрэн жагсаалт." },
          { title: "Тэтгэлэг олох", desc: "Боломжит тэтгэлгүүд, хэрхэн өргөдөл гаргах, шаардлага." },
          { title: "Нийт зардлын тооцоолол", desc: "Сургалт, амьдрал, даатгал, бусад зардлын иж бүрэн тооцоолол." },
        ],
        includes: [
          "Сургуулийн сонголтын хяналтын жагсаалт",
          "Хэлний курсийн визийн заавар",
          "Бүрэн алхам алхмаар заавар",
          "Баримт бичгийн жагсаалт",
          "Тэтгэлгийн мэдээллийн сан",
          "Зардлын тооцооллын хүснэгт",
          "Нэг удаагийн асуулт хариулт",
        ],
        price: "₮59,000",
      },
    },
    ds160: {
      default: {
        icon: FileText,
        title: "Сургалтын DS-160 бөглөх заавар",
        subtitle: "Оюутны визэнд тохирсон бөглөх бүрэн заавар",
        duration: "Нэн даруй нэвтрэх",
        audience: "Оюутны виз мэдүүлэгчид",
        description:
          "Оюутны визийн DS-160 маягт нь аяллын визнээс ялгаатай нарийн шаардлагатай. Сургалтын зорилгоо зөв тодорхойлж, магадлалаа нэмэгдүүлэх бүрэн заавар.",
        steps: [
          { title: "Алдаагүй бөглөх", desc: "Оюутны визийн нийтлэг алдаанууд, тэдгээрээс зайлсхийх арга." },
          { title: "Сургалтын зорилго тодорхойлох", desc: "Сургалтын зорилгоо итгэлтэй, зөв тодорхойлох — татгалзалд хүргэдэг хариултуудаас зайлсхийх." },
          { title: "Магадлалаа нэмэх", desc: "Оюутны визийн магадлалыг нэмэгдүүлэх хариултын стратеги." },
        ],
        includes: [
          "Оюутны DS-160 бүрэн бөглөх заавар",
          "Нийтлэг алдааны жагсаалт",
          "Сургалтын зорилго тодорхойлох загвар",
          "Магадлал нэмэгдүүлэх зөвлөмж",
          "Нэг удаагийн асуулт хариулт",
        ],
        price: "₮39,000",
      },
    },
    "mock-interview": {
      default: {
        icon: Mic,
        title: "Оюутны ярилцлаганд бэлтгэх — Mock Interview",
        subtitle: "Манай зөвлөхтэй 45 минут бэлдэх",
        duration: "45 минут (онлайн)",
        audience: "Оюутны ярилцлагад бэлдэж буй хүмүүс",
        description:
          "Оюутны визийн ярилцлага нь аяллын визнээс ялгаатай онцлог асуултуудтай. Сургалтын зорилго, санхүүгийн чадавхи, буцаж ирэх нотлох баримт — эдгээрийг итгэлтэй хариулах дасгал хийнэ.",
        steps: [
          { title: "Цаг захиалах", desc: "Танд тохиромжтой цагт онлайн уулзалт захиална." },
          { title: "Оюутны бэлтгэл", desc: "Сургалтын зорилго, санхүүгийн нотлох баримтын талаар бэлтгэл хийнэ." },
          { title: "Mock Interview", desc: "45 минутын бодит ярилцлагын дасгал — оюутны визийн онцлог асуултуудтай." },
          { title: "Санал хүсэлт", desc: "Ярилцлагын дараа дэлгэрэнгүй санал хүсэлт, сайжруулах зөвлөмж." },
        ],
        includes: [
          "45 минутын онлайн mock interview",
          "Оюутны визийн онцлог асуулт, хариулт",
          "Англи эсвэл Монгол хэлний сонголт",
          "Санхүүгийн нотлох баримтын зөвлөмж",
          "Ярилцлагын дараах санал хүсэлт",
          "Нэмэлт асуулт хариулт (7 хоног)",
        ],
        price: "₮89,000",
      },
    },
    "full-service": {
      ireland: {
        icon: Star,
        title: "Ирланд сургалтын виз — Манайхаар мэдүүлэх",
        subtitle: "Манай гэрээт сургуулиудад суралцах бүрэн дэмжлэг",
        duration: "Захиалгаас эхлэн",
        audience: "Ирландад суралцахыг хүсэгчид",
        description:
          "Ирландын манай гэрээт сургуулиудад суралцах боломж. Хэлний сургуульд суралцаж байхдаа долоо хоногт 20 цаг хүртэл ажиллах эрхтэй. Манай зүгээс сургуулийн сонголт, элсэлт, визийн мэдүүлэг хүртэл бүгдийг хариуцна.",
        steps: [
          { title: "Сургуулийн сонголт", desc: "Манай гэрээт сургуулиудаас тохиромжтой сургуулийг сонгоно." },
          { title: "Элсэлтийн өргөдөл", desc: "Сургуулийн элсэлтийн өргөдлийг бэлдэж, илгээнэ." },
          { title: "Визийн мэдүүлэг", desc: "Ирландын визийн мэдүүлгийн бүх баримт бичгийг бэлдэж, илгээнэ." },
          { title: "Виз авах", desc: "Виз гарсны дараа Ирландад ирж, сургуулиа эхлэнэ." },
        ],
        includes: [
          "Гэрээт сургуулиудад элсэлт",
          "Визийн баримт бичгийн бүрэн дэмжлэг",
          "7 хоногт 20 цаг ажиллах эрх",
          "Хязгааргүй асуулт хариулт (3 сар)",
          "Татгалзсан тохиолдолд дахин мэдүүлэх дэмжлэг",
        ],
        price: "₮990,000",
        isPrimary: true,
      },
      default: {
        icon: Star,
        title: "Биднээс үйлчилгээ авах — Бүрэн дэмжлэг",
        subtitle: "Бүх үйлчилгээг манай агент таны өмнөөс хийж өгнө",
        duration: "Захиалгаас эхлэн",
        audience: "Бүрэн дэмжлэг хүсэгчид",
        description:
          "Дээрх бүх үйлчилгээнүүдийг манай мэргэжлийн агент таньтай хамтран хийж өгнө. Сургуулийн элсэлтийн өргөдлөөс эхлэн визийн мэдүүлэг, тэтгэлгийн өргөдөл хүртэл бүгдийг хамтран хийнэ.",
        steps: [
          { title: "Анхны зөвлөгөө", desc: "Таны нөхцөл байдлыг үнэлж, хамгийн тохиромжтой сургуулийг санал болгоно." },
          { title: "Элсэлтийн өргөдөл", desc: "Сургуулийн элсэлтийн өргөдлийг бэлдэж, илгээнэ." },
          { title: "DS-160 бөглөх", desc: "Оюутны визийн DS-160 маягтыг алдаагүй бөглөнө." },
          { title: "Ярилцлагад бэлдэх", desc: "Mock interview болон бэлтгэл хийж, ярилцлагад итгэлтэй орно." },
          { title: "Тэтгэлгийн өргөдөл", desc: "Боломжит тэтгэлгүүдэд өргөдөл гаргахад дэмжлэг үзүүлнэ." },
        ],
        includes: [
          "Сургалтын виз өөрөө мэдүүлэх гарын авлага",
          "Сургуулийн элсэлтийн дэмжлэг",
          "DS-160 бөглөх бүрэн дэмжлэг",
          "Mock Interview (45 мин)",
          "Тэтгэлгийн өргөдлийн дэмжлэг",
          "Хязгааргүй асуулт хариулт (3 сар)",
          "Татгалзсан тохиолдолд дахин мэдүүлэх дэмжлэг",
        ],
        price: "₮349,000",
        isPrimary: true,
      },
    },
  },
  work: {
    "work-guide": {
      czech: {
        icon: Briefcase,
        title: "Чех улсын үйлдвэрүүдэд ажиллах — Манайхаар мэдүүлэх",
        subtitle: "Хөдөлмөрийн гэрээ, 12 сар хүртэлхи хугацаанд ажиллах боломж",
        duration: "Захиалгаас эхлэн",
        audience: "Чехт ажиллахыг хүсэгчид",
        description:
          "Чехийн үйлдвэрүүдэд ажиллах, хөдөлмөрийн гэрээ байгуулах боломж. Цаг цаанаас ирнэ — 12 сар хүртэлхи хугацаанд дотоод явна. Зарим хүмүүс маш хурдан явдаг тул эрт захиалах нь зүйтэй. Дансны хуулга, санхүүгийн баталгаа шаардлагагүй — зөвхөн гадаад паспорт болон хөдөлмөрийн гэрээ хангалттай.",
        steps: [
          { title: "Шаардлага хангалт", desc: "18–45 нас, гэмт хэрэгт холбогдоогүй байх, Европын холбооны улсад харалж байгаагүй байх." },
          { title: "Баримт бичиг бэлдэх", desc: "Гадаад паспорт болон хөдөлмөрийн гэрээ хангалттай. Дансны хуулга, санхүүгийн баталгаа хэрэггүй." },
          { title: "Төлбөрийн хуваарилт", desc: "Нийт €2,200. 3 хуваалт авна: урьдчилгаа €800, дараах хоёр хуваалтаар хүлээндэтгон." },
          { title: "Цаг захиалах", desc: "Манай зүгээс танид тохиромжтой ажлын байрыг захиална." },
          { title: "Виз авах", desc: "Виз гарсны дараа Чехт ирж, ажлаа эхлэнэ." },
        ],
        includes: [
          "Гадаад паспорт",
          "Хөдөлмөрийн гэрээ (дансны хуулга, санхүүгийн баталгаа хэрэггүй)",
          "Төлбөр: €2,200 — 3 хуваалт (урьдчилгаа €800)",
          "Цаг цаанаас ирнэ — 12 сар хүртэлхи хугацаанд дотоод явна",
          "Шенгений бүсийн 26 улсад чөлөөтэй зорчих боломж",
          "Манай зүгээсийн бүрэн дэмжлэг",
        ],
        price: "€2,200 (3 хуваалт)",
        isPrimary: true,
      },
      default: {
        icon: Briefcase,
        title: "Ажлын виз өөрөө мэдүүлэх гарын авлага",
        subtitle: "Ажлын виз, ажлын зөвшөөрлийн дэс дараалал",
        duration: "Нэн даруй нэвтрэх",
        audience: "Гадаадад ажиллахыг хүсэгчид",
        description:
          "Гадаадад ажиллах мөрөөдлөө биелүүлэхэд шаардлагатай бүх мэдлэг, заавар. Ажил олгогч олохоос эхлэн ажлын зөвшөөрөл авах хүртэл иж бүрэн гарын авлага.",
        steps: [
          { title: "Ажлын виз, зөвшөөрлийн төрлүүд", desc: "Улс бүрийн ажлын виз, зөвшөөрлийн төрлүүд, шаардлага." },
          { title: "Ажил олгогч олох", desc: "Гадаадын ажил олгогчтой гэрээ байгуулах, урилга авах арга." },
          { title: "Бүрдүүлэх материал", desc: "Ажлын визийн баримт бичгийн бүрэн жагсаалт, шаардлага." },
          { title: "Өргөдөл гаргах", desc: "Ажлын визийн өргөдлийн алхам алхмаар заавар." },
        ],
        includes: [
          "Ажлын виз, зөвшөөрлийн иж бүрэн тайлбар",
          "Ажил олгогч олох зөвлөмж",
          "Баримт бичгийн жагсаалт",
          "Алхам алхмаар заавар",
          "Нэг удаагийн асуулт хариулт",
        ],
        price: "₮69,000",
      },
    },
    immigration: {
      default: {
        icon: GraduationCap,
        title: "Цагаачлал — Байнгын оршин суух",
        subtitle: "Ногоон карт, PR авах боломжуудын иж бүрэн тайлбар",
        duration: "Нэн даруй нэвтрэх",
        audience: "Байнгын оршин суух эрх авахыг хүсэгчид",
        description:
          "Ногоон карт, байнгын оршин суух эрх авах боломжуудын иж бүрэн тайлбар. EB-2 NIW, EB-3, Express Entry болон бусад замуудын шаардлага, дэс дараалал.",
        steps: [
          { title: "EB-2 NIW", desc: "Үндэсний ашиг сонирхолд нийцсэн — мэргэжлийн өндөр ур чадвартай хүмүүст." },
          { title: "EB-3 Ажлын виз", desc: "Мэргэжилтэй болон мэргэжилгүй ажилчдад зориулсан EB-3 замын тайлбар." },
          { title: "Express Entry (Канад)", desc: "Канадын Express Entry системийн CRS оноо нэмэгдүүлэх стратеги." },
          { title: "Visa Bulletin", desc: "Visa Bulletin-ийг хэрхэн уншиж, өөрийн нөхцөл байдалд тохируулах." },
          { title: "Хуулийн зөвлөх", desc: "Шаардлагатай тохиолдолд мэргэжлийн цагаачлалын хуулийн зөвлөхтэй холбох." },
        ],
        includes: [
          "EB-2 NIW шаардлага, боломжийн үнэлгээ",
          "EB-3 замын тайлбар, агентлагуудын жагсаалт",
          "Express Entry CRS оноо нэмэгдүүлэх заавар",
          "Visa Bulletin тайлбар",
          "Хуулийн зөвлөхтэй холбох",
          "Нэг удаагийн зөвлөгөө",
        ],
        price: "₮79,000",
      },
    },
    "work-full-service": {
      czech: {
        icon: Star,
        title: "Чех улсын үйлдвэрүүд ажиллах — Манайхаар мэдүүлэх",
        subtitle: "Хөдөлмөрийн гэрээ, 12 сар хүртэлхи хугацаанд ажиллах боломж",
        duration: "Захиалгаас эхлэн",
        audience: "Чехт ажиллахыг хүсэгчид",
        description:
          "Чехийн үйлдвэрүүд ажиллах, хөдөлмөрийн гэрээ байгуулах боломж. Цаг цаанаас ирнэ 12 сар хүртэлхи хугацаанд дотоод явна. Зарим хүмүүс маш хурдан явдаг тул анхаарулах шаардлагатай зүйл байна. Дансны хуулга, санхүүгийн баталгаа шаардлагдүй — зөвхөн гадаад пасспорт ба хөдөлмөрийн гэрээ хангалтай.",
        steps: [
          { title: "Шаардлага хангалт", desc: "18–45 нас, гэмт хэрэгт холбогдоогүй байх, Европын холбооны улсад харалж байгаагүй байх." },
          { title: "Баримт бичиг бэлдэх", desc: "Гадаад пасспорт ба хөдөлмөрийн гэрээ хангалтай. Дансны хуулга, санхүүгийн баталгаа хэрэггүй." },
          { title: "Төлбөрийн хуваарилт", desc: "Нийт €2,200. 3 хуваалт авна: урьдчилгаа €800, дараах хоёр хуваалтаар хүлээнд тохирдоно." },
          { title: "Цаг захиалах", desc: "Манай зүгээс танид тохиромжтой ажлын байрыг захиална." },
          { title: "Виз авах", desc: "Виз гарсны дараа Чехт ирж, ажлаа эхлэнэ." },
        ],
        includes: [
          "Гадаад пасспорт",
          "Хөдөлмөрийн гэрээ (дансны хуулга, санхүүгийн баталгаа хэрэггүй)",
          "Төлбөр: €2,200 — 3 хуваалт (урьдчилгаа €800)",
          "Цаг цаанаас ирнэ 12 сар хүртэлхи хугацаанд ажиллах боломж",
          "Шенгенийн бүсийн 26 улсад чөлөөтэй зорчих боломж",
          "Манай зүгээсийн бүрэн дэмжлэг",
        ],
        price: "€2,200 (3 хуваалт)",
        isPrimary: true,
      },
      default: {
        icon: Star,
        title: "Биднээс үйлчилгээ авах — Бүрэн дэмжлэг",
        subtitle: "Бүх үйлчилгээг манай агент таны өмнөөс хийж өгнө",
        duration: "Захиалгаас эхлэн",
        audience: "Бүрэн дэмжлэг хүсэгчид",
        description:
          "Ажлын виз, байнгын оршин суух эрхтэй холбоотой бүх үйлчилгээнүүдийг манай мэргэжлийн агент таньтай хамтран хийж өгнө. Ажил олгогч олохоос эхлэн ногоон карт авах хүртэл бүгдийг хамтран хийнэ.",
        steps: [
          { title: "Анхны зөвлөгөө", desc: "Таны нөхцөл байдлыг үнэлж, хамгийн тохиромжтой замыг тодорхойлно." },
          { title: "Ажлын виз дэмжлэг", desc: "Ажлын визийн баримт бичиг, өргөдлийн бүрэн дэмжлэг." },
          { title: "Цагаачлалын зөвлөгөө", desc: "Байнгын оршин суух эрхийн боломжуудыг хамтран судална." },
          { title: "Хуулийн зөвлөхтэй холбох", desc: "Шаардлагатай тохиолдолд мэргэжлийн хуулийн зөвлөхтэй холбоно." },
        ],
        includes: [
          "Ажлын виз өөрөө мэдүүлэх гарын авлага",
          "Цагаачлалын боломжийн иж бүрэн судалгаа",
          "Баримт бичгийн бүрэн дэмжлэг",
          "Хуулийн зөвлөхтэй холбох",
          "Хязгааргүй асуулт хариулт (3 сар)",
        ],
        price: "₮399,000",
        isPrimary: true,
      },
    },
  },
};

/* ── Page component ───────────────────────────────────────────── */
// Map (visaType, serviceId, country) → visaKey used in OrderModal
const SERVICE_VISA_KEY_MAP: Record<string, Record<string, Record<string, string>>> = {
  travel: {
    "self-guide": { usa: "usa-travel", australia: "australia-travel", canada: "canada-travel", schengen: "schengen-travel", default: "usa-travel" },
    ds160:        { default: "usa-travel" },
    "mock-interview": { default: "usa-travel" },
    "status-change":  { default: "usa-travel" },
    "full-service":   { default: "usa-travel" },
  },
  study: {
    "self-guide":     { usa: "usa-study", australia: "australia-study", ireland: "ireland-study", default: "usa-study" },
    ds160:            { default: "usa-study" },
    "mock-interview": { default: "usa-study" },
    "full-service":   { ireland: "ireland-study", default: "usa-study" },
  },
  work: {
    "work-guide":       { czech: "czech-work", default: "usa-travel" },
    immigration:        { default: "usa-travel" },
    "work-full-service": { czech: "czech-work", default: "usa-travel" },
  },
};

export default function ServiceDetailPage() {
  const params = useParams<{ country: string; visaType: string; serviceId: string }>();
  const [, navigate] = useLocation();
  const { openConsultation } = useConsultation();
  const [orderOpen, setOrderOpen] = useState(false);

  const countryMeta: Record<string, { flag: string; name: string }> = {
    usa:       { flag: "🇺🇸", name: "АНУ" },
    australia: { flag: "🇦🇺", name: "Австрали" },
    canada:    { flag: "🇨🇦", name: "Канад" },
    ireland:   { flag: "🇮🇪", name: "Ирланд" },
    czech:     { flag: "🇨🇿", name: "Чех Улс" },
    schengen:  { flag: "🇪🇺", name: "Шенген" },
  };

  const country = countryMeta[params.country] ?? { flag: "🌍", name: params.country };
  const content =
    SERVICE_DB[params.visaType]?.[params.serviceId]?.[params.country] ??
    SERVICE_DB[params.visaType]?.[params.serviceId]?.["default"];

  // Resolve visaKey for OrderModal
  const visaKey =
    SERVICE_VISA_KEY_MAP[params.visaType]?.[params.serviceId]?.[params.country] ??
    SERVICE_VISA_KEY_MAP[params.visaType]?.[params.serviceId]?.["default"] ??
    "usa-travel";

  if (!content) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 100%)" }}
      >
        <Header />
        <div className="text-center text-white/50">
          <p>Хуудас олдсонгүй</p>
          <button onClick={() => navigate("/")} className="mt-4 text-violet-400 underline text-sm">
            Нүүр хуудас руу буцах
          </button>
        </div>
      </div>
    );
  }

  const Icon = content.icon;

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 45%, #0F0B35 100%)" }}
    >
      <Header />

      <div className="container pt-24 pb-20">
        {/* Breadcrumb back */}
        <motion.button
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(`/visa/${params.country}`)}
          className="flex items-center gap-2 mb-8 text-white/50 hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">{country.flag} {country.name} виз руу буцах</span>
        </motion.button>

        <div className="max-w-2xl mx-auto">
          {/* Header card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl p-8 mb-6"
            style={
              content.isPrimary
                ? {
                    background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                    boxShadow: "0 16px 48px rgba(124,58,237,0.35)",
                  }
                : {
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }
            }
          >
            <div className="flex items-start gap-5">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={
                  content.isPrimary
                    ? { background: "rgba(255,255,255,0.18)" }
                    : { background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)" }
                }
              >
                <Icon className={`w-7 h-7 ${content.isPrimary ? "text-white" : "text-violet-400"}`} />
              </div>
              <div className="flex-1">
                <h1
                  className={`font-black text-xl leading-tight mb-1 ${content.isPrimary ? "text-white" : "text-white/90"}`}
                  style={{ fontFamily: "'Sora', var(--font-display), sans-serif", letterSpacing: "-0.02em" }}
                >
                  {content.title}
                </h1>
                <p
                  className="text-sm"
                  style={{ color: content.isPrimary ? "rgba(255,255,255,0.65)" : "rgba(255,255,255,0.45)" }}
                >
                  {content.subtitle}
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <div className="flex items-center gap-1.5">
                    <Clock className={`w-3.5 h-3.5 ${content.isPrimary ? "text-white/60" : "text-violet-400"}`} />
                    <span className="text-xs" style={{ color: content.isPrimary ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.45)" }}>
                      {content.duration}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className={`w-3.5 h-3.5 ${content.isPrimary ? "text-white/60" : "text-violet-400"}`} />
                    <span className="text-xs" style={{ color: content.isPrimary ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.45)" }}>
                      {content.audience}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-6 mb-4"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <p className="text-white/60 text-sm leading-relaxed">{content.description}</p>
          </motion.div>

          {/* Steps */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="rounded-2xl p-6 mb-4"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <h2
              className="text-white/80 font-bold text-sm mb-4"
              style={{ fontFamily: "'Sora', var(--font-display), sans-serif" }}
            >
              Агуулга
            </h2>
            <div className="space-y-4">
              {content.steps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold"
                    style={{ background: "rgba(124,58,237,0.2)", color: "#a78bfa" }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-white/80 font-semibold text-sm">{step.title}</div>
                    <div className="text-white/40 text-xs mt-0.5 leading-relaxed">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Includes */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-6 mb-6"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <h2
              className="text-white/80 font-bold text-sm mb-4"
              style={{ fontFamily: "'Sora', var(--font-display), sans-serif" }}
            >
              Багцад багтах зүйлс
            </h2>
            <div className="space-y-2">
              {content.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white/60 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA — Order */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="rounded-2xl p-6"
            style={{
              background: "linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(79,70,229,0.1) 100%)",
              border: "1px solid rgba(124,58,237,0.25)",
            }}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="text-white/40 text-xs mb-1">Үнэ</div>
                <div
                  className="text-white font-black text-2xl"
                  style={{ fontFamily: "'Sora', var(--font-display), sans-serif" }}
                >
                  {content.price ?? "Үнэ тохирох"}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={openConsultation}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.6)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <Phone className="w-4 h-4" />
                  Зөвлөгөө авах
                </button>
                <button
                    onClick={() => setOrderOpen(true)}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all"
                    style={{
                      background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                      color: "white",
                      boxShadow: "0 4px 16px rgba(124,58,237,0.4)",
                    }}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Захиалах
                  </button>
              </div>
            </div>
            <p className="text-white/30 text-xs">
              Захиалгын дараа манай зөвлөх 24 цагийн дотор холбоо барина.
            </p>
            <div className="mt-3 p-3 rounded-xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="text-white/50 text-xs">
                🏷️ <span className="text-yellow-400/80 font-semibold">Агентын referral код байгаа бол:</span> Захиалгын явцад кодоо оруулна уу — хөнгөлөлтийн дүн төлбөрийн дараа буцааж шилжүүлнэ.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* OrderModal */}
      <OrderModal
        isOpen={orderOpen}
        onClose={() => setOrderOpen(false)}
        preselectedVisaKey={visaKey}
      />
    </div>
  );
}
